import React, { useEffect, useMemo, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { useHistory, useLocation } from 'react-router-dom';
import './ChargeControl.css';
import useChargeStore from '../stores/useChargeStore';
import { useToast } from '../components/Toast/ToastContext';
import LogoSpinner from '../components/ui/LogoSpinner';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import LocationPicker from './LocationPicker';
import GoalControlBar from './GoalControlBar';
import ChargerCard from './ChargerCard';
import ChargerDetail from './ChargerDetail';
import AssignmentsEditor from './AssignmentsEditor';
import RollupChips from './RollupChips';
import ChargeMetrics from './ChargeMetrics';
import ChargerSearch from './ChargerSearch';
import NextChargersCard from './NextChargersCard';
import ChargerInconsistencies from './ChargerInconsistencies';
import ChargeMap from './ChargeMap';
import AssetsTypeList from '../Assets/AssetsTypeList';
import { socColor, aggregateFleet, siteLabel } from './chargeUi';
import type { ChargerView } from './types';

const POLL_MS = 3000;

export default function ChargeControl() {
  const toast = useToast();
  const {
    fleet, locations, sites, prefs, currentLocation, loading, fleetLoaded,
    collectorOk, init, setCurrentLocation, setGoal,
  } = useChargeStore();
  const [openCharger, setOpenCharger] = useState<ChargerView | null>(null);
  const [managing, setManaging] = useState(false);
  const [searching, setSearching] = useState(false);
  const history = useHistory();
  const location = useLocation();
  // View is URL-driven (/charge, /charge/manage, /charge/map) so the browser Back
  // button moves between them instead of being trapped in tab state.
  const view: 'control' | 'asset' | 'map' =
    location.pathname.startsWith('/charge/manage') ? 'asset'
      : location.pathname.startsWith('/charge/map') ? 'map'
        : 'control';
  // Header icon toggles: tapping the active view's button returns to Control.
  const toggleView = (v: 'asset' | 'map') =>
    history.push(view === v ? '/charge' : v === 'asset' ? '/charge/manage' : '/charge/map');

  // Initial load (resumes the user's current location) + 3s poll while open.
  useEffect(() => {
    init();
    const t = setInterval(() => useChargeStore.getState().fetchFleet(), POLL_MS);
    return () => clearInterval(t);
  }, [init]);

  // Keep the open detail drawer in sync with fresh poll data.
  useEffect(() => {
    if (!openCharger) return;
    const fresh = fleet.find((c) => c.vpn_ip === openCharger.vpn_ip);
    if (fresh && fresh !== openCharger) setOpenCharger(fresh);
  }, [fleet]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentName = useMemo(
    () => (currentLocation === 'mine'
      ? 'My Chargers'
      : siteLabel(currentLocation)
        || locations.find((l) => l.id === currentLocation)?.name
        || 'Group'),
    [locations, currentLocation]
  );
  const agg = useMemo(() => aggregateFleet(fleet), [fleet]);
  // Goals disagree across the location → GOAL tile shows an amber em-dash.
  const goalMixed = useMemo(
    () => agg.rollup.goal_pct == null && fleet.some((c) => c.goal_pct != null),
    [agg.rollup.goal_pct, fleet]
  );

  // Live telemetry for the Manage table's "Last seen · Location" column, keyed by
  // asset_id. Only chargers currently in the loaded fleet have live data; the rest
  // of the Manage rows show "—".
  const liveByAssetId = useMemo(() => {
    const m: Record<string, { online: boolean; last_seen: string | null; geo_label: string | null }> = {};
    for (const c of fleet) {
      if (c.asset_id) m[c.asset_id] = { online: c.online, last_seen: c.last_seen, geo_label: c.geo_label };
    }
    return m;
  }, [fleet]);
  // Deep link Manage → Control: open the live detail drawer for a charger by asset_id.
  const openLive = (assetId: string) => {
    const c = fleet.find((x) => x.asset_id === assetId);
    if (c) { setOpenCharger(c); history.push('/charge'); }
  };

  const setLocationGoal = async (percent: number): Promise<boolean> => {
    try {
      const r = await setGoal({ locationId: currentLocation, percent });
      toast.success(`Goal ${percent}% sent to ${r.ok_count}/${r.results.length} charger(s)`);
      return r.ok_count > 0;
    } catch (e: any) {
      toast.error(e?.message || 'Failed to set goal');
      return false;
    }
  };

  const setChargerGoal = async (charger: ChargerView, percent: number): Promise<boolean> => {
    if (!charger.asset_id) { toast.error('Charger has no asset record'); return false; }
    try {
      const r = await setGoal({ chargerIds: [charger.asset_id], percent });
      toast.success(r.ok_count ? `Goal ${percent}% set` : 'Charger did not accept the goal');
      return r.ok_count > 0;
    } catch (e: any) {
      toast.error(e?.message || 'Failed to set goal');
      return false;
    }
  };

  if (loading && !fleetLoaded) return <LogoSpinner label="Loading chargers" />;

  return (
    <div className={`p-4 mx-auto pb-24 ${view === 'control' ? 'max-w-5xl' : 'max-w-7xl'}`}>
      {/* Charge Control is the page; the Map and Manage views are inline toggles in
          the header (each a real URL, so Back works). */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-bold text-primary">Charge Control</h1>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Tooltip label="Live map">
          <button
            type="button" onClick={() => toggleView('map')} aria-pressed={view === 'map'}
            aria-label="Live map"
            className={`p-2 rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              view === 'map' ? 'text-primary-ink bg-primary/10' : 'text-on-surface-variant hover:text-white'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
              <path d="M15 5.764v15" />
              <path d="M9 3.236v15" />
            </svg>
          </button>
          </Tooltip>
          <Tooltip label="Manage chargers">
          <button
            type="button" onClick={() => toggleView('asset')} aria-pressed={view === 'asset'}
            aria-label="Manage chargers"
            className={`p-2 rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              view === 'asset' ? 'text-primary-ink bg-primary/10' : 'text-on-surface-variant hover:text-white'
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 6h4" /><path d="M2 10h4" /><path d="M2 14h4" /><path d="M2 18h4" />
              <rect width="16" height="20" x="4" y="2" rx="2" />
              <path d="M16 2v20" />
            </svg>
          </button>
          </Tooltip>
        </div>
      </div>

      <FadeIn key={view}>
      {view === 'map' && (
        <ChargeMap onOpenCharger={(c) => setOpenCharger(c)} />
      )}

      {view === 'asset' && (<>
        <ChargerInconsistencies />
        <NextChargersCard />
        <AssetsTypeList
          typeKey="smart_charger"
          embedded
          liveByAssetId={liveByAssetId}
          onOpenLive={openLive}
        />
      </>)}

      {view === 'control' && (<>
      <Reveal show={!collectorOk}>
        <Alert tone="error" live="status" className="mb-3">
          Charge collector unreachable. Showing last-known status. Live updates are paused.
        </Alert>
      </Reveal>

      <div className="mb-4">
        <LocationPicker
          locations={locations} sites={sites} current={currentLocation}
          onSelect={setCurrentLocation} onManage={() => setManaging(true)}
          onSearch={() => setSearching(true)}
        />
      </div>

      {/* Location summary + goal control (unified) */}
      <Panel
        title={<span className="block truncate">{currentName}</span>}
        actions={<span className="text-xs text-on-surface-variant flex-shrink-0">{agg.online}/{agg.total} online</span>}
        className="mb-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
          <div className="flex items-end gap-3">
            <div className={`text-3xl font-bold tabular-nums ${socColor(agg.rollup.soc_avg)}`}>
              {agg.rollup.soc_avg != null ? `${Math.round(agg.rollup.soc_avg)}%` : '—'}
            </div>
            <div className="text-xs text-on-surface-variant mb-1">{agg.rollup.present_count} batteries
              {agg.rollup.thermal_warnings > 0 && <span className="text-amber-400"> · {agg.rollup.thermal_warnings} thermal</span>}
            </div>
          </div>
          <ChargeMetrics goal={agg.rollup.goal_pct} etaHours={agg.rollup.eta_hours_max} goalMixed={goalMixed} />
        </div>
        <RollupChips rollup={agg.rollup} />
        <div className="mt-4 pt-4 border-t border-gray-700/60">
          <GoalControlBar
            label={`Set ${currentName} goal`} currentGoal={agg.rollup.goal_pct}
            onSet={setLocationGoal} disabled={agg.total === 0}
          />
        </div>
      </Panel>

      {/* Charger grid — pinned, grouped, or observed at a declared site */}
      <FadeIn key={currentLocation}>
      {fleet.length === 0 ? (
        siteLabel(currentLocation) ? (
          // A site has nothing to curate, so this empty state teaches rather than
          // offering an "add" that would not mean anything.
          <EmptyState
            title={`Nothing connected from ${currentName} right now.`}
            action={<button type="button" onClick={() => setSearching(true)}
              className={buttonClass('tonal', 'md')}>
              Search the fleet</button>}
          >
            Chargers appear here on their own when they connect from that network.
          </EmptyState>
        ) : currentLocation === 'mine' ? (
          <EmptyState
            title="No chargers pinned yet."
            action={<button type="button" onClick={() => setSearching(true)}
              className={buttonClass('filled', 'md')}>
              Search chargers</button>}
          >
            Search the fleet and pin the ones you watch.
          </EmptyState>
        ) : (
          <EmptyState
            action={<button type="button" onClick={() => setManaging(true)}
              className={buttonClass('tonal', 'md')}>Add chargers</button>}
          >
            No chargers in this group yet.
          </EmptyState>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {fleet.map((c) => (
            <ChargerCard key={c.vpn_ip} charger={c} onOpen={() => setOpenCharger(c)} />
          ))}
        </div>
      )}
      </FadeIn>
      </>)}
      </FadeIn>

      {openCharger && (
        <ChargerDetail
          charger={openCharger} hasVpnAccess={prefs.has_vpn_access}
          onSetGoal={(p) => setChargerGoal(openCharger, p)} onClose={() => setOpenCharger(null)}
        />
      )}
      {managing && <AssignmentsEditor onClose={() => setManaging(false)} />}
      {searching && (
        <ChargerSearch
          onClose={() => setSearching(false)}
          onOpenCharger={(c) => { setSearching(false); setOpenCharger(c); }}
        />
      )}
    </div>
  );
}
